'use client';

import { faShareNodes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useState } from 'react';

import ImageWithFallback from '@/components/ImageWithFallback/ImageWithFallback';
import { ConfettiCardBg } from '@/modules/players/v3/components/player-pages/achievements/svgs/confetti-card-bg';
import { AchievementDetailModal } from '@/modules/players/v3/components/shared/achievement-detail-modal/achievement-detail-modal';
import { MilestoneBar } from '@/modules/players/v3/components/shared/milestone-bar/milestone-bar';
import { ShareAchievementModal } from '@/modules/players/v3/components/shared/share-achievement-modal/share-achievement-modal';
import type { Achievement } from '@/modules/players/v3/types/achievements';
import { PICKLEBALL_PLACEHOLDER_IMAGE } from '@/utils/helpers/constants';

interface AchievementCardProps {
  achievement: Achievement;
  showShare?: boolean;
}

export function AchievementCard({ achievement, showShare = true }: AchievementCardProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const isCompleted = achievement.alreadyAchieved;
  const isNotStarted = !achievement.alreadyAchieved && achievement.currentValue === 0;
  const hasMilestones = achievement.milestones.length > 0;
  const description = isCompleted
    ? achievement.accomplishedDescription || achievement.objectiveDescription
    : achievement.objectiveDescription;

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        className='group border border-gray-200 rounded-2xl overflow-hidden flex flex-col bg-white cursor-pointer hover:border-gray-300 transition-colors'
        onClick={() => setDetailOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setDetailOpen(true);
        }}
      >
        <div className='flex items-start gap-2 p-2.5 relative overflow-hidden'>
          {isCompleted ? (
            <ConfettiCardBg className='absolute top-0 left-0 w-full h-full pointer-events-none' />
          ) : null}

          <div className='size-20 shrink-0 rounded-xl overflow-hidden relative'>
            <ImageWithFallback
              src={achievement.logo ?? undefined}
              fallbackSrc={PICKLEBALL_PLACEHOLDER_IMAGE}
              alt={achievement.achievementName}
              className={clsx(
                'size-full object-contain transition-[filter] duration-200',
                isNotStarted && 'grayscale group-hover:grayscale-0'
              )}
            />
          </div>

          <div className='min-w-0 flex flex-col gap-0.5 flex-1 pt-0.5'>
            <span className='font-semibold text-sm text-gray-900 line-clamp-2'>
              {achievement.achievementName}
            </span>
            <span className='text-xs text-gray-600 line-clamp-2'>{description}</span>
          </div>

          {isCompleted && showShare ? (
            <button
              type='button'
              aria-label={`Share ${achievement.achievementName}`}
              className='shrink-0'
              onClick={(e) => {
                e.stopPropagation();
                setShareOpen(true);
              }}
            >
              <FontAwesomeIcon icon={faShareNodes} className='text-gray-400 size-4' />
            </button>
          ) : null}
        </div>

        <div
          className={clsx(
            'h-11 px-3 flex items-center justify-between gap-3',
            isCompleted && 'bg-gray-100 border-t border-gray-200'
          )}
        >
          {isCompleted ? (
            <>
              <span
                className='font-black italic text-brand-700 text-sm inline-block uppercase'
                style={{ transform: 'scaleY(1.3)' }}
              >
                Completed
              </span>
              {hasMilestones ? (
                <span className='shrink-0 text-xs font-semibold text-success-700 bg-success-50 border border-success-200 rounded-full px-2 py-0.5'>
                  {`${achievement.currentValue} / ${achievement.goalValue}`}
                </span>
              ) : null}
            </>
          ) : isNotStarted && !hasMilestones ? (
            <div className='relative h-5 w-full bg-gray-200 rounded-full flex items-center justify-center'>
              <span className='text-xs text-gray-500'>Not started</span>
            </div>
          ) : hasMilestones ? (
            <>
              <div className='flex-1'>
                <MilestoneBar milestones={achievement.milestones} />
              </div>
              <span
                className={clsx(
                  'shrink-0 text-xs whitespace-nowrap',
                  isNotStarted ? 'text-gray-400' : 'text-gray-600'
                )}
              >
                {`${achievement.currentValue} / ${achievement.goalValue}`}
              </span>
            </>
          ) : (
            <span className='text-xs text-gray-600 ml-auto'>
              {`${achievement.currentValue} / ${achievement.goalValue}`}
            </span>
          )}
        </div>
      </div>

      <AchievementDetailModal
        achievement={achievement}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        onShare={showShare ? () => setShareOpen(true) : undefined}
        showShare={showShare}
      />

      {showShare ? (
        <ShareAchievementModal
          achievements={[achievement]}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      ) : null}
    </>
  );
}
